import React, { useState, useEffect } from 'react';
import { Save, Loader2, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';

const VideoManagement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    loadVideoData();
  }, []);

  const loadVideoData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/projects');
      if (response.data && response.data.length > 0) {
        setYoutubeUrl(response.data[0].youtube_url || '');
        setProjectId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading video:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (projectId) {
        await api.put(`/projects/${projectId}`, { youtube_url: youtubeUrl });
      } else {
        const response = await api.post('/projects', { 
          title: 'Projet Principal',
          description: 'Description du projet',
          youtube_url: youtubeUrl 
        });
        setProjectId(response.data.id);
      }
      
      toast.success('Vidéo YouTube sauvegardée avec succès !');
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Video className="w-6 h-6" />
          Vidéo YouTube de Présentation
        </h2>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </>
          )}
        </Button>
      </div>

      <div className="glass rounded-xl p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            URL de la Vidéo YouTube (format embed)
          </label>
          <Input
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white"
            placeholder="https://www.youtube.com/embed/VIDEO_ID"
          />
          <p className="text-xs text-gray-400 mt-2">
            ⚠️ Important : Utilisez le format "embed" de YouTube
          </p>
          <p className="text-xs text-gray-400">
            Exemple : https://www.youtube.com/embed/dQw4w9WgXcQ
          </p>
        </div>

        {youtubeUrl && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Aperçu de la vidéo
            </label>
            <div className="aspect-video rounded-lg overflow-hidden bg-slate-800">
              <iframe
                src={youtubeUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h3 className="text-white font-medium mb-2">Comment obtenir l'URL embed ?</h3>
          <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
            <li>Allez sur votre vidéo YouTube</li>
            <li>Cliquez sur "Partager"</li>
            <li>Cliquez sur "Intégrer"</li>
            <li>Copiez l'URL qui commence par "https://www.youtube.com/embed/"</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default VideoManagement;
